import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import type { components } from "@dolfin/api-admin";
import { useDolfinClient } from "@/components/DolfinProvider";
import { InfoHint } from "@/components/InfoHint";
import { Eyebrow } from "@/components/PageUi";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { apiErrorMessage } from "@/lib/api-error";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";

type CustomerResponse = components["schemas"]["CustomerResponse"];
export type CustomerChannel = components["schemas"]["CustomerChannel"];

const CHANNEL_LABELS: Record<CustomerChannel, string> = {
  Email: "Email",
  WhatsApp: "WhatsApp",
  Both: "Email and WhatsApp",
};

const FALLBACK_HINT =
  "Without consent, or whenever WhatsApp cannot be used, the customer gets invoices and reminders by email instead.";

/** Field message for 400 Customer.InvalidWhatsAppPhone (matched inside the validation body), else null. */
export function whatsAppPhoneError(error: unknown): string | null {
  if (!JSON.stringify(error ?? {}).includes("Customer.InvalidWhatsAppPhone")) return null;
  // Only when the number is the sole problem: an error on any other field must not be hidden under this one.
  const errors = (error as { errors?: Record<string, unknown> } | undefined)?.errors;
  const fields = errors && typeof errors === "object" ? Object.keys(errors) : [];
  if (fields.some((field) => !/whatsappphone/i.test(field))) return null;
  return apiErrorMessage(error, "Enter the number in international format, e.g. +44 7700 900123.");
}

export function CustomerWhatsAppFields({
  idPrefix,
  phone,
  onPhoneChange,
  channel,
  onChannelChange,
  error,
  hasConsent,
}: {
  idPrefix: string;
  phone: string;
  onPhoneChange: (value: string) => void;
  channel: CustomerChannel;
  onChannelChange: (value: CustomerChannel) => void;
  error: string | null;
  /** The saved customer has consent, which a new number withdraws: said beside the field. */
  hasConsent?: boolean;
}) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-whatsapp`}>WhatsApp number</Label>
        <Input
          id={`${idPrefix}-whatsapp`}
          type="tel"
          placeholder="+44 7700 900123"
          value={phone}
          onChange={(e) => onPhoneChange(e.target.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${idPrefix}-whatsapp-error` : undefined}
        />
        {error ? (
          <p id={`${idPrefix}-whatsapp-error`} className="text-xs text-destructive">
            {error}
          </p>
        ) : hasConsent ? (
          <p className="text-xs text-muted-foreground">Changing the number withdraws the customer's consent.</p>
        ) : null}
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-channel`} className="flex items-center gap-1">
          Send invoices by
          <InfoHint label="More about the preferred channel">
            {FALLBACK_HINT} Record consent on the customer's page after saving the number.
          </InfoHint>
        </Label>
        <select
          id={`${idPrefix}-channel`}
          className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
          value={channel}
          onChange={(e) => onChannelChange(e.target.value as CustomerChannel)}
        >
          {(Object.keys(CHANNEL_LABELS) as CustomerChannel[]).map((c) => (
            <option key={c} value={c}>
              {CHANNEL_LABELS[c]}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

/** Consent is a separate call from the customer save: it is a claim a person makes, and the backend records who. */
export function CustomerWhatsAppCard({
  customer,
  onChange,
}: {
  customer: CustomerResponse;
  onChange: (customer: CustomerResponse) => void;
}) {
  const api = useDolfinClient();
  const [busy, setBusy] = useState(false);
  const number = customer.whatsAppPhone ?? null;
  const consented = !!customer.hasWhatsAppConsent;

  async function setConsent(give: boolean) {
    if (busy || !customer.id) return;
    setBusy(true);
    const params = { params: { path: { id: customer.id } } };
    const { data, error } = give
      ? await api.POST("/v1/customers/{id}/whatsapp-consent", params)
      : await api.DELETE("/v1/customers/{id}/whatsapp-consent", params);
    setBusy(false);
    if (error || !data) {
      toast.error(
        JSON.stringify(error ?? {}).includes("Customer.WhatsAppPhoneRequired")
          ? "Save a WhatsApp number for this customer first."
          : apiErrorMessage(error, give ? "Could not record consent." : "Could not withdraw consent."),
      );
      return;
    }
    onChange(data);
    toast.success(give ? "Consent recorded." : "Consent withdrawn. This customer now gets email only.");
  }

  return (
    <div className="rounded-xl border bg-card/60 p-5 backdrop-blur-sm">
      <div className="mb-3 flex items-center gap-1">
        <Eyebrow>WhatsApp</Eyebrow>
        <InfoHint label="More about WhatsApp delivery">
          {FALLBACK_HINT} Changing the customer's WhatsApp number withdraws consent.
        </InfoHint>
      </div>
      <div className="space-y-2.5">
        <Row label="Number" value={number ?? <span className="text-muted-foreground">Not set</span>} mono={!!number} />
        <Row label="Send invoices by" value={CHANNEL_LABELS[customer.preferredChannel ?? "Email"]} />
        <Row label="Consent" value={<ConsentStatus customer={customer} />} />
      </div>

      <div className="mt-4 border-t pt-4">
        {consented ? (
          <Button variant="outline" size="sm" disabled={busy} onClick={() => setConsent(false)}>
            {busy && <Spinner className="size-4" />}
            Withdraw consent
          </Button>
        ) : (
          <label
            className={cn(
              "flex items-start gap-2.5 text-sm",
              number && !busy ? "cursor-pointer" : "cursor-not-allowed text-muted-foreground",
            )}
          >
            <Checkbox
              className="mt-0.5"
              checked={false}
              disabled={!number || busy}
              onCheckedChange={(checked) => checked === true && setConsent(true)}
            />
            <span>
              The customer agreed to receive invoices and reminders on WhatsApp
              {!number ? (
                <span className="mt-0.5 block text-xs">Add a WhatsApp number with Edit first.</span>
              ) : null}
            </span>
            {busy && <Spinner className="ml-auto size-4" />}
          </label>
        )}
      </div>
    </div>
  );
}

function ConsentStatus({ customer }: { customer: CustomerResponse }) {
  if (customer.hasWhatsAppConsent && customer.whatsAppConsentAt) {
    const by = customer.whatsAppConsentSource === "Payer" ? "by the customer" : "by your team";
    return (
      <span className="text-emerald-600 dark:text-emerald-400">
        Recorded {formatDate(customer.whatsAppConsentAt)} ({by})
      </span>
    );
  }
  if (customer.whatsAppOptedOutAt) {
    return <span className="text-amber-600 dark:text-amber-400">Opted out {formatDate(customer.whatsAppOptedOutAt)}</span>;
  }
  return <span className="text-muted-foreground">No consent yet</span>;
}

function Row({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn("text-right font-medium", mono && "font-mono text-xs")}>{value}</span>
    </div>
  );
}
